import {
  CommonActions,
  CompositeScreenProps,
  getFocusedRouteNameFromRoute,
  NavigationProp,
  Route,
  useNavigation,
} from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import useInvitationHandlers from '@components/Chat/useInvitationHandlers';
import { CoreProviders } from '@components/CoreProviders';
import { ChatGroupWelcomeModal } from '@components/Modals/ChatGroupWelcomeModal.web';
import { Box } from '@components/Restyle';
import { createWebDrawerNavigator } from '@components/Web/Navigator/WebDrawerNavigator';
import {
  Invitation,
  ListTeamsDocument,
  PublicChat,
  UserRequestCategory,
  UserRequestable,
  useAcceptChatInvitationMutation,
  useAcceptTeamInvitationMutation,
  useAcceptUserInviteMutation,
  useCreateChatMutation,
  useCreateUserRequestMutation,
  useGetPublicProjectLazyQuery,
  useInvitationDetailsLazyQuery,
  useJoinRequestedTaskMutation,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import useActiveChat, { ActiveChat } from '@hooks/useActiveChat';
import useMe from '@hooks/useMe';
import { useProjectUpdatedBroadcast } from '@hooks/useProjectUpdatedBroadcast';
import { ChatsStackParamList } from '@navigation/chats/chats-stack';
import {
  ContactsStack,
  ContactsTabBarParamList,
} from '@navigation/contacts/contacts-stack.web';
import { GlobalActivityStack } from '@navigation/GlobalActivity/GlobalActivityStack';
import HelpStack from '@navigation/help-stack';
import { NestedNavigatorParams } from '@navigation/helpers';
import { decidePostSignupNavigation } from '@navigation/postSignupNavigation';
import ProfileStack from '@navigation/profile/profile-stack.web';
import {
  ProjectsStack,
  ProjectStackParamsList,
} from '@navigation/projects/projects-stack.web';
import { TabNavigatorParamsList } from '@navigation/tab-navigator';
import { TasksStackParamsList } from '@navigation/tasks/tasks-stack';
import TasksTabStack from '@navigation/tasks/tasks-tab-stack';
import { decideTeamInviteAuthAction } from '@navigation/teamInviteAuthNavigation';
import { decideTeamLinkLoginNavigation } from '@navigation/teamLinkLoginNavigation';
import { useAppNavigation } from '@navigation/useAppNavigation';
import GlobalSearch from '@screens/Global/GlobalSearch';
import { TaskWelcomeScreen } from '@screens/Tasks/TaskWelcomeScreen.web';
import { TeamWelcomeScreen } from '@screens/Teams/TeamWelcomeScreen.web';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import {
  decideGenericInviteAction,
  GenericInviteAction,
} from '@utils/genericInvite';
import {
  clearPendingGenericInvitation,
  clearStoredGenericInvitation,
  getPendingGenericInvitation,
  setPendingGenericInvitation,
} from '@utils/invitationWebUrl';
import { logError } from '@utils/logNonFatalError';
import { isMobileBrowser } from '@utils/mobileDeepLink';
import { openPendingInviterChat } from '@utils/pendingInviterChat';
import { submitTeamJoinRequest } from '@utils/pendingTeamJoinRequests';

import PaymentsStack from './payments/payments-stack';
/**
 * Params for nested stacks
 */
export type AppStackParamList = {
  home: NestedNavigatorParams<TabNavigatorParamsList>;
  'chats-stack': NestedNavigatorParams<ChatsStackParamList>;
  'projects-stack': NestedNavigatorParams<ProjectStackParamsList>;
  'tasks-stack': NestedNavigatorParams<TasksStackParamsList>;
  'contacts-stack': NestedNavigatorParams<ContactsTabBarParamList>;
  'global-search': undefined;
  'my-account': undefined;
  'help-stack': undefined;
  'payments-stack': undefined;
  'globalActivity-stack': undefined;
  'team-welcome': { teamId: string };
  'task-welcome': { taskId: string; isPostRequest: boolean };
};

/**
 * Params for direct app stack
 */
export type AppStackScreenParams = {
  home: undefined;
  'chats-stack': ChatsStackParamList['chat-detail'];
  'projects-stack': undefined;
  'tasks-stack': undefined;
  'files-stack': undefined;
  'contacts-stack': undefined;
  'global-search': undefined;
  'my-account': undefined;
  'help-stack': undefined;
  'payments-stack': undefined;
  'globalActivity-stack': undefined;
  'team-welcome': { teamId: string };
  'task-welcome': { taskId: string; isPostRequest: boolean };
};

/**
 * Screen props for combined app stack and nested stacks
 */
export type AppStackScreenProps<T extends keyof AppStackParamList> =
  CompositeScreenProps<
    StackScreenProps<AppStackScreenParams, T>,
    StackScreenProps<AppStackParamList | AppStackScreenParams>
  >;

const Stack = createWebDrawerNavigator<
  AppStackParamList | AppStackScreenParams
>();

export const AppStack = () => {
  const chatInviteStorageKeys = [
    'invitation_token',
    'link_type',
    'type',
    'task_or_project_id',
    'pending_chat_invitation_token',
    'pending_chat_invitation_id',
  ] as const;
  const { t } = useTranslation('models');
  const toast = useToast();
  // Handlers created by mount-only effects below would otherwise keep
  // ToastProvider's initial placeholder ({}); read the live toast instead.
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);
  const { trackEvent } = useMixpanel();

  const { setActiveChat } = useActiveChat();
  const getProjectHeaderTitle = (route: Route<string, object | undefined>) => {
    const routeName =
      getFocusedRouteNameFromRoute(route) ?? t('models:navigation.projects');

    switch (routeName) {
      case 'new':
        return t('models:projects.create.title');
      default:
        return t('models:navigation.projects');
    }
  };

  const { me } = useMe();
  const getTasksHeaderTitle = (route: Route<string, object | undefined>) => {
    const routeName =
      getFocusedRouteNameFromRoute(route) ?? t('models:navigation.myTasks');

    switch (routeName) {
      case 'task-create':
        return t('models:tasks.create.title');
      default:
        return t('models:navigation.myTasks');
    }
  };

  const pendingProjectUuid = window.sessionStorage.getItem(
    'pending_project_uuid'
  );
  const teamInvitationCode = window.sessionStorage.getItem('invitation_token');
  const pendingChatInvitationToken =
    window.sessionStorage.getItem('pending_chat_invitation_token') ?? '';
  const pendingChatInvitationId =
    window.sessionStorage.getItem('pending_chat_invitation_id') ?? '';

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  const updateOnboardingState1 = () => {
    window.sessionStorage.setItem('skip_welcome_modal', 'true');
    updateOnboardingState({
      variables: {
        attributes: {
          welcomeModalSeen: true,
          taskCreationComplete: true,
          projectCreationComplete: true,
          inviteMemberComplete: true,
          checklistCreationComplete: true,
        },
      },
      update: (cache) => {
        if (!me?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            onboardingState: (existing) => ({
              ...existing,
              welcomeModalSeen: true,
              taskCreationComplete: true,
              projectCreationComplete: true,
              inviteMemberComplete: true,
              checklistCreationComplete: true,
            }),
          },
        });
      },
    });
  };
  // When an authenticated user clicks a copied invite link (e.g. /invited-user?type=task&id=28020)
  // while already logged in, AuthStack never runs so sessionStorage is never populated.
  // Read URL params here as a fallback so the invitation useEffect can handle task links.
  const urlInviteParams = (() => {
    if (typeof window === 'undefined') return null;
    if (window.location.pathname !== '/invited-user') return null;
    const p = new URLSearchParams(window.location.search);
    const type = p.get('type') ?? '';
    const id = p.get('id') ?? '';
    const token = p.get('invitation_token') ?? p.get('token') ?? '';
    const requestId = p.get('request_id') ?? '';
    if (!type && !id && !token) return null;
    return { type, id, token, requestId };
  })();

  // When urlInviteParams is set (/invited-user path), the URL is fully
  // authoritative — don't fall back to sessionStorage even if type is
  // missing/empty, to avoid mis-routing from stale or inherited-tab values.
  const tokenType = (() => {
    if (urlInviteParams) return urlInviteParams.type || null;
    if (pendingChatInvitationToken && pendingChatInvitationId) return 'chat';
    return (
      window.sessionStorage.getItem('link_type') ||
      window.sessionStorage.getItem('type') ||
      null
    );
  })();

  // TA-11035 / TA-11131: an existing user arrived with a generic Contacts
  // invite — either already signed in on /invited-user?invitation_token=…
  // (no type), or just logged in from that link (the login screen parked the
  // token). Accept it so the user is connected with the inviter. A fresh
  // sign-up never gets here: registration already used the token.
  const [acceptUserInvite] = useAcceptUserInviteMutation();
  const [genericInviteAction, setGenericInviteAction] =
    useState<GenericInviteAction | null>(null);
  useEffect(() => {
    const urlToken =
      urlInviteParams?.token && !urlInviteParams.type
        ? urlInviteParams.token
        : null;
    if (urlToken) clearStoredGenericInvitation();
    const invitationToken = urlToken || getPendingGenericInvitation();
    if (!invitationToken) return;

    // Hold the token until the server answers: a failed request is retried
    // on the next app load instead of silently losing the invite.
    setPendingGenericInvitation(invitationToken);
    acceptUserInvite({ variables: { invitationToken } })
      .then(({ data }) => decideGenericInviteAction(data?.acceptUserInvite))
      .catch((error: unknown) => {
        logError({
          message: 'acceptUserInvite failed for a generic invite',
          errorMessage: error instanceof Error ? error.message : String(error),
          operationName: 'acceptUserInvite',
        });
        return decideGenericInviteAction(null);
      })
      .then((action) => {
        if (action.kind !== 'retry') clearPendingGenericInvitation();
        setGenericInviteAction(action);
      });
  }, []);

  // ToastProvider only hands out a usable toast from its own mount effect,
  // which runs after this child's mount effects — so act on the result here,
  // where `toast` is live, rather than in the mount effect above.
  useEffect(() => {
    if (!genericInviteAction) return;
    if (genericInviteAction.kind === 'open-chat') {
      openChatInviteChat(undefined, genericInviteAction.chatId);
      setGenericInviteAction(null);
      return;
    }
    if (typeof toast.show !== 'function') return;
    toast.show(t(`models:${genericInviteAction.messageKey}`), {
      duration: 3000,
    });
    setGenericInviteAction(null);
  }, [genericInviteAction, toast]);

  const navigation =
    useNavigation<NavigationProp<AppStackParamList | AppStackScreenParams>>();
  const appNavigation = useAppNavigation();
  const { handleClearTeamInvitationToken, handleOnErrorInvitation } =
    useInvitationHandlers();
  // TA-11139: feeds the projectUpdated event web My Tasks refreshes on.
  useProjectUpdatedBroadcast();

  // Chat invitation flow
  const chatInviteChatId =
    pendingChatInvitationId ||
    window.sessionStorage.getItem('task_or_project_id') ||
    '';

  const taskRequestTaskId = window.sessionStorage.getItem('task_or_project_id');
  const [showChatJoinModal, setShowChatJoinModal] = useState(false);
  const [invitedChatInfo, setInvitedChatInfo] = useState<
    PublicChat | undefined
  >();

  const [isProcessingChatInvitation, setIsProcessingChatInvitation] =
    useState(false);

  const clearChatInviteToken = () => {
    chatInviteStorageKeys.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
  };

  const getChatInvitationToken = () =>
    pendingChatInvitationToken || teamInvitationCode;

  const openChatInviteChat = (chat?: ActiveChat, chatId?: string) => {
    const targetChatId = chat?.id ?? chatId ?? chatInviteChatId;
    if (!targetChatId) return;

    window.sessionStorage.setItem('notification_open_chat', 'true');
    window.sessionStorage.setItem('notification_chat_id', targetChatId);
    window.sessionStorage.setItem('pending_welcome_chat_id', '');

    if (chat) {
      setActiveChat(chat);
    }

    navigation.navigate('chats-stack', {
      chatId: targetChatId,
    } as any);
  };

  // TA-10891 #21: a member who joins a project from the Owner's email invite
  // lands with the Owner's chat already open (see utils/pendingInviterChat).
  const [createInviterChat] = useCreateChatMutation();
  useEffect(() => {
    void openPendingInviterChat({
      meId: me?.id,
      // Only the inviter — backend adds the current user as chat owner.
      createChat: async (inviterId) =>
        (
          await createInviterChat({
            variables: { attributes: { userIds: [inviterId] } },
          })
        ).data?.createChat,
      openChat: (chat) => openChatInviteChat(chat as ActiveChat),
    });
  }, [me?.id]);

  const [
    acceptChatInvitation,
    { data: acceptChatInvitationData, error: acceptChatInvitationError },
  ] = useAcceptChatInvitationMutation();

  useEffect(() => {
    if (!acceptChatInvitationData) return;
    clearChatInviteToken();
    setIsProcessingChatInvitation(false);
    setShowChatJoinModal(false);
    const chat = acceptChatInvitationData.acceptChatInvitation;
    if (chat?.id) {
      // TA-10886: the trial welcome modal is gone, so there is nothing left
      // to defer the chat behind — open it straight away
      openChatInviteChat(chat as ActiveChat);
    }
  }, [acceptChatInvitationData]);

  useEffect(() => {
    if (!acceptChatInvitationError) return;
    clearChatInviteToken();
    setIsProcessingChatInvitation(false);
    setShowChatJoinModal(false);
    if (
      acceptChatInvitationError.message.includes(
        'You are already a member of this chat'
      )
    ) {
      toast.show('You are already a member of this chat', {
        duration: 3000,
      });
    }
  }, [acceptChatInvitationError]);

  const [
    getChatInvitationDetails,
    {
      data: getChatInvitationDetailsData,
      error: getChatInvitationDetailsError,
    },
  ] = useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (!getChatInvitationDetailsData) return;
    const invitationToken = getChatInvitationToken();
    if (!invitationToken) {
      setIsProcessingChatInvitation(false);
      return;
    }

    if (getChatInvitationDetailsData.invitationDetails?.autoJoin === true) {
      acceptChatInvitation({
        variables: { invitationToken },
      });
      return;
    } else {
      if (getChatInvitationDetailsData.invitationDetails?.chat) {
        setInvitedChatInfo(getChatInvitationDetailsData.invitationDetails.chat);
        setShowChatJoinModal(true);
      } else {
        // No usable chat payload and not an auto-join — clear the token so
        // the effect cannot re-trigger on the next render.
        clearChatInviteToken();
      }
    }

    setIsProcessingChatInvitation(false);
  }, [getChatInvitationDetailsData]);

  useEffect(() => {
    if (!getChatInvitationDetailsError) return;
    clearChatInviteToken();
    setIsProcessingChatInvitation(false);
    setShowChatJoinModal(false);
  }, [getChatInvitationDetailsError]);

  useEffect(() => {
    if (
      isProcessingChatInvitation ||
      tokenType !== 'chat' ||
      showChatJoinModal
    ) {
      return;
    }

    const invitationToken = getChatInvitationToken();
    if (!invitationToken) {
      return;
    }

    setIsProcessingChatInvitation(true);
    getChatInvitationDetails({
      variables: {
        token: invitationToken,
        type: Invitation.Chat,
      },
    });
  }, [
    acceptChatInvitation,
    getChatInvitationDetails,
    isProcessingChatInvitation,
    pendingChatInvitationToken,
    showChatJoinModal,
    tokenType,
    teamInvitationCode,
  ]);

  const [
    getInvitationDetails,
    { data: getInvitationDetailsData, error: getInvitationDetailsError },
  ] = useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (!getInvitationDetailsData) return;
    const teamUUId = getInvitationDetailsData.invitationDetails?.team?.uuid;
    if (teamUUId) {
      // Navigate to team detail page
      (navigation as any).navigate('my-account', {
        screen: 'subscriptionPlanDetail',
        params: {
          teamUUId: teamUUId,
        },
      });
    } else {
      navigation.navigate('chats-stack', {} as any);
    }
  }, [getInvitationDetailsData]);

  useEffect(() => {
    if (!getInvitationDetailsError) return;
    navigation.navigate('chats-stack', {} as any);
  }, [getInvitationDetailsError]);

  // Fallback for scoped email-invite signup when team UUID wasn't in sessionStorage.
  // Navigates to the welcome screen (mobile) or chats (desktop) after resolving UUID.
  const [
    getInvitationDetailsForScopedSignup,
    {
      data: getInvitationDetailsForScopedSignupData,
      error: getInvitationDetailsForScopedSignupError,
    },
  ] = useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (!getInvitationDetailsForScopedSignupData) return;
    const teamUUId =
      getInvitationDetailsForScopedSignupData.invitationDetails?.team?.uuid;
    if (teamUUId) {
      if (isMobileBrowser()) {
        (navigation as any).navigate('team-welcome', { teamId: teamUUId });
      } else {
        navigation.navigate('chats-stack', {} as any);
      }
    } else {
      // UUID not resolvable — user signed up successfully so land in chats.
      navigation.navigate('chats-stack', {} as any);
    }
  }, [getInvitationDetailsForScopedSignupData]);

  useEffect(() => {
    if (!getInvitationDetailsForScopedSignupError) return;
    // Invitation details fetch failed — user already signed up so navigate
    // to a safe landing screen rather than leaving them on a blank screen.
    navigation.navigate('chats-stack', {} as any);
  }, [getInvitationDetailsForScopedSignupError]);

  // Resolves team UUID + autoJoin for authenticated users clicking a team invite
  // link directly (/invited-user?type=team&invitation_token=...). Used to
  // distinguish email invites (auto-accept) from link invites (show team preview).
  const [
    getTeamInviteDetailsForAuthUser,
    {
      data: teamInviteDetailsForAuthData,
      error: teamInviteDetailsForAuthError,
    },
  ] = useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (!teamInviteDetailsForAuthData) return;
    const token =
      urlInviteParams?.token ||
      window.sessionStorage.getItem('invitation_token') ||
      '';
    const action = decideTeamInviteAuthAction({
      teamUUId: teamInviteDetailsForAuthData.invitationDetails?.team?.uuid,
      autoJoin: teamInviteDetailsForAuthData.invitationDetails?.autoJoin,
      token,
    });

    const clearInviteKeys = () => {
      window.sessionStorage.removeItem('invitation_token');
      window.sessionStorage.removeItem('link_type');
      window.sessionStorage.removeItem('task_or_project_id');
      // The accept-invite effects fall back to team_preview_uuid when
      // task_or_project_id is empty, so leaving it behind here would let this
      // invite's team leak into the next one handled in the same tab.
      // 'accept' deliberately skips this — acceptTeamInvitationData still
      // needs the key and clears it itself.
      window.sessionStorage.removeItem('team_preview_uuid');
    };

    switch (action.kind) {
      case 'accept':
        // Email invite — seed task_or_project_id so acceptTeamInvitationData
        // can navigate after the mutation succeeds. Let that effect handle cleanup.
        window.sessionStorage.setItem('task_or_project_id', action.teamUUId);
        acceptTeamInvitationMutation({
          variables: { invitationToken: action.token },
          refetchQueries: [{ query: ListTeamsDocument }],
        });
        break;
      case 'preview':
        // Link invite — navigate directly to the public team detail page.
        clearInviteKeys();
        (navigation as any).navigate('my-account', {
          screen: 'subscriptionPlanDetail',
          params: { teamUUId: action.teamUUId },
        });
        break;
      case 'none':
        // Could not resolve team — clean up stale keys and bail.
        clearInviteKeys();
        break;
    }
  }, [teamInviteDetailsForAuthData]);

  useEffect(() => {
    if (!teamInviteDetailsForAuthError) return;
    // Invalid/expired token or network failure — clean up stale keys so they
    // don't interfere with future navigation, then land on a safe screen.
    window.sessionStorage.removeItem('invitation_token');
    window.sessionStorage.removeItem('link_type');
    window.sessionStorage.removeItem('task_or_project_id');
    window.sessionStorage.removeItem('task_join_request_id');
    // Same reason as clearInviteKeys above: the accept-invite effects fall
    // back to this key, so a failed resolve must not leave it behind either.
    window.sessionStorage.removeItem('team_preview_uuid');
    navigation.navigate('chats-stack', {} as any);
  }, [teamInviteDetailsForAuthError]);

  const [createUserRequest] = useCreateUserRequestMutation();
  const [joinRequestedTask] = useJoinRequestedTaskMutation();

  const [
    getPublicProjectForInvite,
    {
      data: getPublicProjectForInviteData,
      error: getPublicProjectForInviteError,
    },
  ] = useGetPublicProjectLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!getPublicProjectForInviteData) return;
    const rawProject = getPublicProjectForInviteData?.getPublicProject ?? null;
    const sessionProjectId =
      window.sessionStorage.getItem('task_or_project_id') ?? '';
    // Prefer UUID from the response directly; present on both Project and
    // PublicProject so it's more reliable than re-reading sessionStorage.
    const projectUUId = rawProject?.uuid || sessionProjectId;

    window.sessionStorage.removeItem('invitation_token');
    window.sessionStorage.removeItem('link_type');
    window.sessionStorage.removeItem('task_or_project_id');

    if (!projectUUId) return;

    // Already a member — backend returns Project instead of PublicProject;
    // navigate directly without creating a MemberAddition request.
    if (rawProject?.__typename === 'Project') {
      navigation.navigate('projects-stack', {
        screen: 'details',
        params: { projectUUId },
      } as any);
      return;
    }

    const pubProject =
      rawProject?.__typename === 'PublicProject' ? rawProject : null;
    const invitationType = pubProject?.invitationType ?? null;

    if (!invitationType) return;

    // Link invites: navigate to the project page and let the user request
    // membership manually from there. Do not auto-submit a MemberAddition.
    if (invitationType === 'link') {
      navigation.navigate('projects-stack', {
        screen: 'details',
        params: { projectUUId },
      } as any);
      return;
    }

    // Email invites: the user accepted by clicking the link, so submit the
    // MemberAddition request automatically before navigating.
    (async () => {
      try {
        await createUserRequest({
          variables: {
            attributes: {
              requestType: UserRequestCategory.MemberAddition,
              userRequestableId: pubProject!.id,
              userRequestableType: UserRequestable.Project,
            },
          },
        });
      } catch (err: any) {
        // Email-invited users should join directly; a failure here means the
        // invitation is invalid or already consumed — surface it.
        toast.show(
          err?.graphQLErrors?.[0]?.message ?? 'Failed to join project.',
          { type: 'danger' }
        );
        return;
      }

      navigation.navigate('projects-stack', {
        screen: 'details',
        params: { projectUUId },
      } as any);
    })();
  }, [getPublicProjectForInviteData]);

  useEffect(() => {
    if (!getPublicProjectForInviteError) return;
    window.sessionStorage.removeItem('invitation_token');
    window.sessionStorage.removeItem('link_type');
    window.sessionStorage.removeItem('task_or_project_id');
  }, [getPublicProjectForInviteError]);

  const [
    acceptTeamInvitationMutation,
    { data: acceptTeamInvitationData, error: acceptTeamInvitationError },
  ] = useAcceptTeamInvitationMutation();

  useEffect(() => {
    if (!acceptTeamInvitationData) return;
    const run = async () => {
      // Read all context before clearing session storage
      const teamUUID =
        window.sessionStorage.getItem('task_or_project_id') ||
        window.sessionStorage.getItem('team_preview_uuid');
      const isTeamScopedSignup =
        window.sessionStorage.getItem('invite_signup') === '1' &&
        window.sessionStorage.getItem('invite_signup_type') === 'team';
      const isLinkInviteScopedSignup =
        window.sessionStorage.getItem('is_link_invite') === '1';
      // Read token before handleClearTeamInvitationToken removes it
      const invitationToken = window.sessionStorage.getItem('invitation_token');

      trackEvent(MIXPANEL_EVENTS.TEAM_INVITE_ACCEPTED, {
        team_id: teamUUID,
        invite_type: isLinkInviteScopedSignup ? 'link' : 'email',
      });

      await handleClearTeamInvitationToken();
      window.sessionStorage.removeItem('team_preview_uuid');
      window.sessionStorage.removeItem('invite_signup');
      window.sessionStorage.removeItem('invite_signup_type');
      window.sessionStorage.removeItem('is_link_invite');

      if (isTeamScopedSignup && !isLinkInviteScopedSignup && teamUUID) {
        // Email-invite scoped signup — show welcome screen on mobile,
        // otherwise land in chats
        if (isMobileBrowser()) {
          (navigation as any).navigate('team-welcome', { teamId: teamUUID });
        } else {
          navigation.navigate('chats-stack', {} as any);
        }
      } else if (teamUUID) {
        (navigation as any).navigate('my-account', {
          screen: 'subscriptionPlanDetail',
          params: { teamUUId: teamUUID },
        });
      } else if (
        isTeamScopedSignup &&
        !isLinkInviteScopedSignup &&
        invitationToken
      ) {
        // UUID wasn't in sessionStorage (query hadn't resolved before login).
        // Resolve from the token and navigate to the appropriate post-signup dest.
        getInvitationDetailsForScopedSignup({
          variables: { token: invitationToken, type: Invitation.Team },
        });
      } else if (invitationToken) {
        // Existing user logged in — no UUID available, resolve from token.
        getInvitationDetails({
          variables: { token: invitationToken, type: Invitation.Team },
        });
      }
    };
    run();
  }, [acceptTeamInvitationData]);

  useEffect(() => {
    if (!acceptTeamInvitationError) return;
    const run = async () => {
      // Read both UUID and token before handleOnErrorInvitation zeroes the keys
      const teamUUID =
        window.sessionStorage.getItem('task_or_project_id') ||
        window.sessionStorage.getItem('team_preview_uuid');
      const invitationToken = window.sessionStorage.getItem('invitation_token');

      handleOnErrorInvitation(acceptTeamInvitationError);
      window.sessionStorage.removeItem('team_preview_uuid');
      window.sessionStorage.removeItem('invite_signup');
      window.sessionStorage.removeItem('invite_signup_type');

      if (teamUUID) {
        // UUID stored by TeamInvitationPreviewScreen — use it directly so we
        // still navigate even when the invitation token has expired.
        (navigation as any).navigate('my-account', {
          screen: 'subscriptionPlanDetail',
          params: { teamUUId: teamUUID },
        });
      } else if (invitationToken) {
        // Fallback: query invitation details to resolve the team UUID
        getInvitationDetails({
          variables: { token: invitationToken, type: Invitation.Team },
        });
      }
    };
    run();
  }, [acceptTeamInvitationError]);

  useEffect(() => {
    // One decision table for both Team link shapes reaching an existing user
    // after login — see teamLinkLoginNavigation.ts. Computed once and
    // dispatched from the two team arms below.
    const teamLinkLogin = decideTeamLinkLoginNavigation({
      inviteSignup: window.sessionStorage.getItem('invite_signup'),
      linkType: tokenType,
      invitationToken: teamInvitationCode,
      teamInviteAutoJoin: window.sessionStorage.getItem(
        'team_invite_auto_join'
      ),
      teamPreviewUuid: window.sessionStorage.getItem('team_preview_uuid'),
      taskOrProjectId: window.sessionStorage.getItem('task_or_project_id'),
    });

    if (pendingProjectUuid) {
      window.sessionStorage.removeItem('pending_project_uuid');
      // On mobile, task email invite navigates to task-welcome (handled by
      // the post-signup effect below). Skip the project navigation so the user
      // doesn't land on the project page first.
      const isTaskEmailInviteMobile =
        isMobileBrowser() &&
        window.sessionStorage.getItem('invite_signup') === '1' &&
        window.sessionStorage.getItem('invite_signup_type') === 'task' &&
        !!window.sessionStorage.getItem('post_invite_open_task_id');
      if (!isTaskEmailInviteMobile) {
        // Mark onboarding complete for users who arrive via a project invite link
        // so they skip the onboarding checklist flow after registration.
        if (window.sessionStorage.getItem('invite_signup') === '1') {
          updateOnboardingState1();
        }
        navigation.navigate('projects-stack', {
          screen: 'details',
          params: { projectUUId: pendingProjectUuid },
        } as any);
      }
    } else if (
      teamInvitationCode &&
      teamInvitationCode.length > 0 &&
      tokenType === 'team'
    ) {
      if (window.sessionStorage.getItem('invite_signup') === '1') {
        updateOnboardingState1();
      }
      if (urlInviteParams) {
        // Authenticated user clicked a team invite link directly.
        // app-navigator.tsx populates invitation_token but never sets
        // team_invite_auto_join, so the flag-based path below cannot be used.
        // Query invitationDetails first to check autoJoin: accept immediately
        // for email invites, navigate to the public team preview for link invites
        // so the user can explicitly click "Request to Join".
        getTeamInviteDetailsForAuthUser({
          variables: { token: urlInviteParams.token, type: Invitation.Team },
        });
      } else {
        // Existing post-signup / post-preview flows — use the stored flag.
        const teamInviteAutoJoin =
          window.sessionStorage.getItem('team_invite_auto_join') === '1';

        if (teamInviteAutoJoin) {
          // Email invite — accept immediately
          window.sessionStorage.removeItem('team_invite_auto_join');
          acceptTeamInvitationMutation({
            variables: { invitationToken: teamInvitationCode },
            refetchQueries: [{ query: ListTeamsDocument }],
          });
        } else if (
          window.sessionStorage.getItem('team_invite_auto_join') !== null
        ) {
          // Link token (team_invite_auto_join='0'). Clear the flag here so it
          // doesn't linger.
          window.sessionStorage.removeItem('team_invite_auto_join');

          if (teamLinkLogin.kind === 'resolve-token') {
            // TA-10773: an existing user who logged in from the link has no
            // other handler — the invite_signup effect below only runs for
            // registrations, so this used to do nothing at all. Resolve the
            // invitation so decideTeamInviteAuthAction routes it; a link
            // invite yields 'preview', landing on the team page where
            // NonMemberContent offers "Request to Join".
            getTeamInviteDetailsForAuthUser({
              variables: {
                token: teamLinkLogin.invitationToken,
                type: Invitation.Team,
              },
            });
          }
          // A registration falls through to the invite_signup effect, which
          // calls createUserRequest and navigates.
        } else {
          // Legacy path: no team_invite_auto_join flag (UUID-only flow or old token flow)
          acceptTeamInvitationMutation({
            variables: { invitationToken: teamInvitationCode },
            refetchQueries: [{ query: ListTeamsDocument }],
          });
        }
      }
    } else if (tokenType === 'team') {
      // No invitation_token, so the branch above didn't fire: this is the
      // UUID-only Team link (/my-account/subscriptionPlanDetail/{uuid}/
      // TeamDetails) that an existing logged-out user just signed in from.
      // Send them to the team page, where NonMemberContent offers
      // "Request to Join" — previously they landed nowhere (TA-10773 item 0).
      if (teamLinkLogin.kind === 'team-detail') {
        teamLinkLogin.keysToClear.forEach((key) =>
          window.sessionStorage.removeItem(key)
        );
        (navigation as any).navigate('my-account', {
          screen: 'subscriptionPlanDetail',
          params: { teamUUId: teamLinkLogin.teamUUId },
        });
      }
    } else if (tokenType === 'project') {
      const projectId = window.sessionStorage.getItem('task_or_project_id');
      if (window.sessionStorage.getItem('invite_signup') === '1') {
        updateOnboardingState1();
      }
      if (projectId) {
        getPublicProjectForInvite({
          variables: {
            id: projectId,
            token: teamInvitationCode || undefined,
          },
        });
      }
    } else if (tokenType === 'task') {
      // Authenticated user clicked a task invite link (/invited-user?type=task&id=...).
      // AuthStack never ran so sessionStorage is empty — fall back to URL params.
      // Only use sessionStorage.task_or_project_id when there are no URL invite
      // params at all. If urlInviteParams is set but has no id (malformed link),
      // don't fall through to a potentially stale sessionStorage value.
      const taskId = urlInviteParams
        ? urlInviteParams.id
        : (window.sessionStorage.getItem('task_or_project_id') ?? '');
      const taskJoinRequestId = urlInviteParams
        ? urlInviteParams.requestId
        : (window.sessionStorage.getItem('task_join_request_id') ?? '');
      // Captured before removal below so it can be handed off to
      // TasksTabStack — it uses invitationDetails to populate the task/
      // project name shown on the no-access "Request to Join" fallback,
      // since this effect can't reach WebDrawerContext directly (see the
      // sessionStorage bridge comment on pending_open_task_panel_id below).
      const inviteToken = urlInviteParams
        ? urlInviteParams.token
        : (window.sessionStorage.getItem('invitation_token') ?? '');
      window.sessionStorage.removeItem('invitation_token');
      window.sessionStorage.removeItem('link_type');
      window.sessionStorage.removeItem('task_or_project_id');
      window.sessionStorage.removeItem('task_join_request_id');
      const xTaskId = taskId === '' ? taskRequestTaskId : taskId;
      if (xTaskId) {
        if (window.sessionStorage.getItem('invite_signup') === '1') {
          updateOnboardingState1();
        }
        const openTask = () => {
          if (isMobileBrowser()) {
            (navigation as any).navigate('task-welcome', {
              taskId: xTaskId,
              isPostRequest: false,
            });
          } else {
            // AppStack's useEffect is outside WebDrawerProvider, so calling
            // navigateToTask (which calls setIdTaskDetailOpen/setIsTaskWebPanelOpen)
            // would hit no-op default context setters. Use the sessionStorage bridge
            // instead: TasksTabStack reads pending_open_task_panel_id on mount and
            // opens the panel from inside WebDrawerProvider — without the
            // newly_joined_task_id side-effect that shows join guidance UI.
            window.sessionStorage.setItem(
              'pending_open_task_panel_id',
              xTaskId
            );
            if (inviteToken) {
              window.sessionStorage.setItem(
                'pending_open_task_panel_invite_token',
                inviteToken
              );
            }
            navigation.navigate('tasks-stack');
          }
        };

        // WebDrawerContext mirrors its panel state into sessionStorage
        // ('screenInfo') on every change, so it can be read here even though
        // this effect runs outside WebDrawerProvider.
        const isTaskPanelAlreadyOpen = () => {
          const screenInfo = window.sessionStorage.getItem('screenInfo');
          if (!screenInfo) return false;
          try {
            const { isTaskWebPanelOpen, idTaskDetailOpen } =
              JSON.parse(screenInfo);
            return isTaskWebPanelOpen && idTaskDetailOpen === xTaskId;
          } catch {
            return false;
          }
        };

        if (taskJoinRequestId) {
          (async () => {
            try {
              await joinRequestedTask({
                variables: { userRequestId: taskJoinRequestId },
              });
            } catch (err: any) {
              toastRef.current.show?.(
                err?.graphQLErrors?.[0]?.message ?? 'Failed to join task chat.',
                { type: 'danger' }
              );
            }
            if (!isTaskPanelAlreadyOpen()) openTask();
          })();
        } else {
          openTask();
        }
      }
    }
  }, []);

  useEffect(() => {
    const action = decidePostSignupNavigation({
      inviteSignup: window.sessionStorage.getItem('invite_signup'),
      scopedTokenType: window.sessionStorage.getItem('link_type'),
      taskOrProjectId: window.sessionStorage.getItem('task_or_project_id'),
      isLinkInvite: window.sessionStorage.getItem('is_link_invite') === '1',
    });

    const clearKeys = (keys: string[]) =>
      keys.forEach((k) => window.sessionStorage.removeItem(k));

    // Every non-'none' action carries the exact pre/post key sets to clear.
    // The effect never hard-codes a key list — postSignupNavigation.ts is
    // the single source of truth for what gets removed and when.
    switch (action.kind) {
      case 'task-link-welcome':
        clearKeys(action.keysToClearBefore);
        updateOnboardingState1();
        // Task link-invite: user submitted a join request (pending approval).
        // On mobile: navigate to task-welcome with pending state so the user
        // sees the task overview + "Request Sent" bottom sheet.
        // On desktop: open the task side panel (shows the request-sent guidance card).
        if (isMobileBrowser()) {
          (navigation as any).navigate('task-welcome', {
            taskId: action.taskId,
            isPostRequest: true,
          });
        } else {
          appNavigation.navigateToTask(
            { id: action.taskId, projectId: '' },
            false,
            true
          );
        }
        clearKeys(action.keysToClearAfter);
        break;

      case 'task-email-noop':
        // Task email invite: desktop navigation handled by pending_project_uuid
        // effect above. On mobile, navigate to task-welcome so the user sees the
        // "You've joined this task" confirmation sheet instead of the project page.
        if (isMobileBrowser()) {
          const mobileTaskId = window.sessionStorage.getItem(
            'post_invite_open_task_id'
          );
          if (mobileTaskId) {
            window.sessionStorage.removeItem('post_invite_open_task_id');
            clearKeys(action.keysToClearBefore);
            clearKeys(action.keysToClearAfter);
            (navigation as any).navigate('task-welcome', {
              taskId: mobileTaskId,
              isPostRequest: false,
            });
            break;
          }
        }
        clearKeys(action.keysToClearBefore);
        updateOnboardingState1();
        clearKeys(action.keysToClearAfter);
        break;

      case 'project-scoped':
        // Backend creates the UserRequest atomically during link-invite
        // registration; no FE call needed here.
        clearKeys(action.keysToClearBefore);
        updateOnboardingState1();
        (navigation as any).navigate('projects-stack', {
          screen: 'details',
          params: { projectUUId: action.projectUUId },
        });
        clearKeys(action.keysToClearAfter);
        break;

      case 'team-link-invite':
        // Team link-invite: send a join request then navigate to the team
        // detail page so the user can see their pending status. Backend may
        // also create the UserRequest atomically during registration, so we
        // catch duplicates gracefully.
        (async () => {
          clearKeys(action.keysToClearBefore);
          updateOnboardingState1();
          // Never throws, so navigation below is not blocked: the request
          // may already exist from the backend, which creates it during
          // link-invite registration. TA-10772 — that duplicate still means
          // the user is pending, and submitTeamJoinRequest records it. The
          // team detail page it navigates to cannot learn this from
          // getPublicTeam, whose hasPendingMemberAdditionRequest comes back
          // false in this flow. A genuine failure records nothing and leaves
          // the button on that page actionable.
          await submitTeamJoinRequest({
            teamUUId: action.teamUUId,
            createRequest: () =>
              createUserRequest({
                variables: {
                  attributes: {
                    requestType: UserRequestCategory.MemberAddition,
                    userRequestableId: action.teamUUId,
                    userRequestableType: UserRequestable.Account,
                  },
                },
              }),
          });
          clearKeys(action.keysToClearAfter);
          (navigation as any).navigate('my-account', {
            screen: 'subscriptionPlanDetail',
            params: { teamUUId: action.teamUUId },
          });
        })();
        break;

      case 'none':
        break;
    }
  }, []);

  // Only check URL path for initial route - removed goto_welcome === '-1' check
  // to ensure all users land on projects-stack after signup for tutorialization
  const isFromMyProfile = window.location.pathname.startsWith('/my-account');
  return (
    <Box style={{ height: '100vh', maxHeight: '100vh' }}>
      <CoreProviders>
        <Stack.Navigator
          initialRouteName={isFromMyProfile ? 'my-account' : 'projects-stack'}>
          <Stack.Screen
            name='projects-stack'
            component={ProjectsStack}
            options={({ route }) => ({
              iconProps: { name: 'Folder', activeName: 'FolderActive' },
              drawerLabel: t('navigation.projects'),
              title: getProjectHeaderTitle(route),
              unmountOnBlur: true,
              headerTransparent: true,
            })}
          />
          <Stack.Screen
            name='tasks-stack'
            component={TasksTabStack}
            options={({ route }) => ({
              iconProps: { name: 'TTHashtag', activeName: 'TTHashtagActive' },
              drawerLabel: t('navigation.myTasks'),
              title: getTasksHeaderTitle(route),
              unmountOnBlur: true,
            })}
          />
          <Stack.Screen
            name='globalActivity-stack'
            component={GlobalActivityStack}
            options={{
              iconProps: {
                name: 'ActivityDot',
                activeName: 'ActivityDotActive',
              },
              drawerLabel: t('activity.activity'),
              title: t('activity.globalActivityLog'),
              unmountOnBlur: true,
              headerTransparent: true,
            }}
          />
          <Stack.Screen
            name='contacts-stack'
            component={ContactsStack}
            options={() => ({
              iconProps: { name: 'User', activeName: 'UserActive' },
              drawerLabel: t('navigation.contacts'),
              title: t('navigation.contacts'),
              unmountOnBlur: true,
              headerTransparent: true,
            })}
          />
          <Stack.Screen
            name='global-search'
            component={GlobalSearch}
            options={{
              drawerLabel: t('navigation.search'),
              hideMenuItem: true,
            }}
          />

          <Stack.Screen
            name='help-stack'
            options={{
              iconProps: { name: 'HelpCircle' },
              drawerLabel: t('navigation.help'),
              showBelowTheFold: true,
            }}
            component={HelpStack}
          />

          <Stack.Screen
            name='my-account'
            component={ProfileStack}
            options={() => ({
              showBelowTheFold: true,
              unmountOnBlur: true,
              drawerLabel: t('navigation.myAccount'),
              drawerIcon: () => {
                const { avatar, firstName = '', lastName = '' } = me || {};
                const initials =
                  (firstName?.charAt(0).toLocaleLowerCase() || '') +
                  (lastName?.charAt(0).toLocaleLowerCase() || '');
                return (
                  <Avatar avatar={avatar} label={initials} size='medium' />
                );
              },
            })}
            listeners={({ navigation }) => ({
              drawerItemPress: (e) => {
                e.preventDefault();
                navigation.dispatch(
                  CommonActions.reset({
                    index: 0,
                    routes: [
                      {
                        name: 'my-account',
                        params: {
                          screen: 'tabs',
                          params: {
                            screen: 'my-profile',
                          },
                        },
                      },
                    ],
                  })
                );
              },
            })}
          />
          <>
            {
              // TODO make this a hidden nav item
            }
          </>
          <Stack.Screen
            name='payments-stack'
            component={PaymentsStack}
            options={{
              iconProps: { name: 'Office' },
              hideMenuItem: true,
              drawerLabel: 'Payments',
            }}
          />
          <Stack.Screen
            name='team-welcome'
            component={TeamWelcomeScreen}
            options={{
              hideMenuItem: true,
              drawerLabel: 'Welcome',
            }}
          />
          <Stack.Screen
            name='task-welcome'
            component={TaskWelcomeScreen}
            options={{
              hideMenuItem: true,
              drawerLabel: 'Welcome',
            }}
          />
        </Stack.Navigator>
      </CoreProviders>
      {showChatJoinModal && invitedChatInfo && (
        <ChatGroupWelcomeModal
          chatInfo={invitedChatInfo}
          visible={showChatJoinModal}
          onClose={() => {
            clearChatInviteToken();
            setShowChatJoinModal(false);
          }}
          onAccept={() => {
            const invitationToken = getChatInvitationToken();
            if (!invitationToken) return;
            setIsProcessingChatInvitation(true);
            acceptChatInvitation({
              variables: { invitationToken },
              onCompleted: () => {
                clearChatInviteToken();
                setIsProcessingChatInvitation(false);
                setShowChatJoinModal(false);
              },
              onError: (error) => {
                clearChatInviteToken();
                setIsProcessingChatInvitation(false);
                setShowChatJoinModal(false);
                if (
                  error.message.includes(
                    'You are already a member of this chat'
                  )
                ) {
                  openChatInviteChat();
                }
              },
            });
          }}
        />
      )}
    </Box>
  );
};
